/* eslint-disable indent */
/* eslint-disable react/jsx-indent */
import React, { SetStateAction, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DeleteIcon, QuestionOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Spinner,
  Switch,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { useAppSelector } from '../../../../redux/hooks';
import { updateActiveStep as updateManualVerificationActiveStep } from '../../../../redux/manualVerification/stepperDetails';
import { updateProviderDetails } from '../../../../redux/providerBuilder/providerDetails';
import { getProviderDetails } from '../../../../redux/providerBuilder/providerDetails/selectors';
import {
  deleteSelectedInstance,
  updateResponseSelection,
} from '../../../../redux/providerBuilder/selectedInstances';
import { getSelectedInstances } from '../../../../redux/providerBuilder/selectedInstances/selectors';
import {
  getSelectedNetworkIndex,
  getSelectedNetworkRequest,
} from '../../../../redux/providerBuilder/selectedNetworkRequest/selectors';
import {
  generateProviderDescriptionFromGPT,
  generateProviderNameFromGPT,
} from '../../../../utils/provider-builder/request-config-utils';

export const ManualVerificationProviderConfigScreen = () => {
  const countryCodes = require('../../../../utils/data/country-codes.json');

  const dispatch = useDispatch();

  const selectedInstances = useAppSelector(getSelectedInstances);
  const providerDetails = useAppSelector(getProviderDetails);
  const userType = useAppSelector((state) => state.user.userType);
  const selectedNetworkRequest = useAppSelector(getSelectedNetworkRequest);
  const selectedNetworkIndex = useAppSelector(getSelectedNetworkIndex);

  const [providerName, setProviderName] = useState(
    providerDetails.name ? providerDetails.name : ''
  );
  const [providerDescription, setProviderDescription] = useState(
    providerDetails.description ? providerDetails.description : ''
  );
  const [stepsToFollow, setStepsToFollow] = useState(
    providerDetails.stepsToFollow ? providerDetails.stepsToFollow : ''
  );
  const [nameError, setNameError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [buttonLoading, setButtonLoading] = useState(false);
  const [providerNameLoading, setProviderNameLoading] = useState(false);
  const [providerDetailsLoading, setProviderDetailsLoading] = useState(false);
  const [showAdvanceFields, setShowAdvanceFields] = useState<boolean>(false);
  const [customInjection, setCustomInjection] = useState(
    providerDetails?.customInjection ?? ''
  );
  const [geoLocation, setGeoLocation] = useState(providerDetails?.geoLocation ?? '');
  const [providerType, setProviderType] = useState(
    providerDetails?.providerType ?? 'PRIVATE'
  );

  useEffect(() => {
    const setDefaultValues = async () => {
      setProviderNameLoading(true);
      setProviderDetailsLoading(true);
      const providerName = await generateProviderNameFromGPT(
        selectedNetworkRequest[selectedNetworkIndex]?.url,
        selectedInstances
      );
      setProviderName(providerName);
      setProviderNameLoading(false);
      const providerDescription = await generateProviderDescriptionFromGPT(
        providerName,
        selectedNetworkRequest[selectedNetworkIndex]?.url,
        selectedInstances
      );
      setProviderDescription(providerDescription);
      setProviderDetailsLoading(false);
    };

    if (!providerName && !providerDescription) {
      setDefaultValues();
    }
  }, []);

  useEffect(() => {
    const allResponseSelections = selectedInstances.map(
      (instance: any) => instance.responseSelection
    );
    dispatch(updateProviderDetails({ responseSelections: allResponseSelections }));
  }, [selectedInstances]);

  function handleGoBack() {
    dispatch(updateManualVerificationActiveStep(5));
  }

  function handleNext() {
    if (!providerName) {
      setNameError('Provider name is required');
      return;
    }

    if (!providerDescription) {
      setDescriptionError('Provider description is required');
      return;
    }

    setButtonLoading(true);
    dispatch(
      updateProviderDetails({
        name: providerName,
        description: providerDescription,
        method: selectedNetworkRequest[selectedNetworkIndex]?.method,
        stepsToFollow,
        providerType,
        customInjection,
        geoLocation,
        isApproved: !customInjection || userType === 'ADMIN',
      })
    );

    dispatch(updateManualVerificationActiveStep(7));
  }

  return (
    <Flex direction="column" width="100%" h="100%" justifyContent="space-between">
      <Card variant="outline" flex={1} borderBottomRadius={0} borderBottom="none">
        <CardBody p={0}>
          <VStack
            spacing={5}
            align="stretch"
            h="calc(100vh - 240px)"
            overflowY="scroll"
            overflowX="hidden"
            p={7}
          >
            <Box width="100%">
              <Flex direction="column" gap="2">
                <Heading className="regular-font">User Experience</Heading>
                <Text className="medium-font" lineHeight="24px" color="#98A2B3">
                  The following information will help guide your users correctly
                </Text>
              </Flex>
            </Box>
            <Flex w="full" direction="column" gap={5}>
              <Flex justifyContent="flex-start" gap={12}>
                <FormControl isInvalid={!!nameError} w="400px">
                  <Text color="gray.600" className="small-font" ml="5px">
                    Provider Name
                  </Text>
                  <InputGroup>
                    <Input
                      onChange={(e) => {
                        setNameError('');
                        setProviderName(e.target.value);
                      }}
                      placeholder="Provider Name"
                      required={true}
                      value={providerName}
                      px={5}
                      py={6}
                      borderRadius={15}
                      fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                    />
                    {providerNameLoading && (
                      <InputRightElement width="4.5rem">
                        <Spinner />
                      </InputRightElement>
                    )}
                  </InputGroup>
                  <FormErrorMessage>{nameError}</FormErrorMessage>
                </FormControl>
                <FormControl w="400px" isInvalid={!!descriptionError}>
                  <Text color="gray.600" className="small-font" ml="5px">
                    Provider Description
                  </Text>
                  <InputGroup>
                    <Input
                      onChange={(e) => {
                        setDescriptionError('');
                        setProviderDescription(e.target.value);
                      }}
                      placeholder="Provider Description"
                      required={true}
                      value={providerDescription}
                      px={5}
                      py={6}
                      borderRadius={15}
                      fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                    />
                    {providerDetailsLoading && (
                      <InputRightElement width="4.5rem">
                        <Spinner />
                      </InputRightElement>
                    )}
                  </InputGroup>
                  <FormErrorMessage>{descriptionError}</FormErrorMessage>
                </FormControl>
              </Flex>
              <Flex direction="column">
                <Text color="gray.600" className="small-font" ml="5px">
                  Steps user needs to follow to open the information page
                </Text>
                <Textarea
                  w="400px"
                  px={5}
                  py={6}
                  borderRadius={15}
                  placeholder="One step per line"
                  value={stepsToFollow}
                  onChange={(e) => setStepsToFollow(e.target.value)}
                  fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                />
              </Flex>
              <Flex>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0" color="gray.600">
                    Advance Options
                  </FormLabel>
                  <Switch
                    id="email-alerts"
                    isChecked={showAdvanceFields}
                    colorScheme="blackAlpha"
                    onChange={() => setShowAdvanceFields(!showAdvanceFields)}
                  />
                </FormControl>
              </Flex>
              {showAdvanceFields && (
                <>
                  <Flex justifyContent="flex-start" gap={10}>
                    <Flex direction="column">
                      <Text ml="5px" color="gray.600" className="small-font">
                        Geolocation
                      </Text>
                      <FormControl id="geolocation" w="400px">
                        <Select
                          placeholder="None"
                          value={geoLocation}
                          onChange={(e) => {
                            setGeoLocation(e.target.value);
                          }}
                          fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                          style={{
                            color: 'black',
                            borderRadius: '15px',
                            border: '1px solid #D0D5DD',
                            backgroundColor: 'transparent',
                            padding: '6px 5px',
                            paddingLeft: '20px',
                            paddingRight: '35px',
                            height: '50px',
                          }}
                        >
                          {countryCodes.map(
                            (countryCode: { Code: string; Name: string }) => (
                              <option key={countryCode.Code} value={countryCode.Code}>
                                {countryCode.Name}
                              </option>
                            )
                          )}
                        </Select>
                      </FormControl>
                    </Flex>
                    <Flex direction="column">
                      <Text color="gray.600" className="small-font">
                        Provider Type
                      </Text>
                      <FormControl id="provider-type" w="400px">
                        <Tooltip
                          label={
                            providerType === 'PUBLIC'
                              ? 'Public Providers Need Successful Proof Generation to be published'
                              : ''
                          }
                          aria-label="A tooltip"
                        >
                          <Select
                            value={providerType}
                            onChange={(e) => {
                              setProviderType(e.target.value);
                            }}
                            fontSize="clamp(10px, calc(0.7vw * 1.4), 18px)"
                            style={{
                              color: 'black',
                              borderRadius: '15px',
                              border: '1px solid #D0D5DD',
                              backgroundColor: 'transparent',
                              padding: '6px 5px',
                              paddingLeft: '20px',
                              paddingRight: '35px',
                              height: '50px',
                            }}
                          >
                            <option value="PRIVATE">Private</option>

                            <option value="PUBLIC">Public</option>
                          </Select>
                        </Tooltip>
                      </FormControl>
                    </Flex>
                  </Flex>
                  <Flex>
                    <Box w="full">
                      <Flex gap="50px">
                        <Box flex={1}>
                          <Flex gap={5}>
                            <Flex direction="column" gap={2} py={5} px={1} flex={1}>
                              <Heading
                                color="gray.500"
                                className="small-font"
                                fontWeight={600}
                                ml="5px"
                              >
                                Variable Details
                              </Heading>

                              <Flex direction="column" gap={5}>
                                {selectedInstances.map((instance: any, i: any) => (
                                  <Card
                                    variant="outline"
                                    key={i}
                                    position="relative"
                                    borderRadius={20}
                                  >
                                    {selectedInstances.length > 1 && (
                                      <IconButton
                                        aria-label="Search database"
                                        icon={<DeleteIcon fontSize="1rem" />}
                                        position="absolute"
                                        right={4}
                                        top={3}
                                        variant="outline"
                                        colorScheme="black"
                                        size="sm"
                                        onClick={() => {
                                          dispatch(deleteSelectedInstance(i));
                                        }}
                                      />
                                    )}
                                    <CardBody>
                                      <Flex direction="column" gap={2}>
                                        <Flex
                                          alignItems="center"
                                          // mt={7}
                                        >
                                          <Flex alignItems="center" w="95%">
                                            <Text
                                              style={{ textWrap: 'nowrap' }}
                                              color="gray.600"
                                              whiteSpace="nowrap"
                                              textOverflow="ellipsis"
                                              minW="150px"
                                            >
                                              Variable Name:
                                            </Text>
                                            <Text
                                              border="1px solid #D0D5DD"
                                              px={5}
                                              py={3}
                                              borderRadius="15px"
                                              color="#000"
                                              whiteSpace="nowrap"
                                              textOverflow="ellipsis"
                                              overflow="hidden"
                                              w="full"
                                              // cursor='not-allowed'
                                            >
                                              {instance?.variableName ?? ''}
                                            </Text>
                                          </Flex>
                                        </Flex>
                                        <Flex alignItems="center" w="95%">
                                          <Text
                                            style={{ textWrap: 'nowrap' }}
                                            color="gray.600"
                                            whiteSpace="nowrap"
                                            textOverflow="ellipsis"
                                            minW="150px"
                                          >
                                            Regex:
                                          </Text>

                                          <Input
                                            value={
                                              instance.responseSelection.responseMatch
                                            }
                                            style={{
                                              color: 'black',
                                              borderRadius: '15px',
                                              border: '1px solid #D0D5DD',
                                            }}
                                            px={5}
                                            py={6}
                                            borderRadius={15}
                                            onChange={(e) => {
                                              dispatch(
                                                updateResponseSelection({
                                                  index: i,
                                                  responseSelection: {
                                                    xPath:
                                                      instance.responseSelection.xPath,
                                                    jsonPath:
                                                      instance.responseSelection.jsonPath,
                                                    responseMatch: e.target.value,
                                                    invariant: instance.invariant,
                                                    matchType:
                                                      instance.responseSelection
                                                        .matchType,
                                                  },
                                                })
                                              );
                                            }}
                                          />
                                        </Flex>

                                        <Flex alignItems="center" w="95%">
                                          <Text
                                            style={{ textWrap: 'nowrap' }}
                                            color="gray.600"
                                            whiteSpace="nowrap"
                                            textOverflow="ellipsis"
                                            minW="150px"
                                          >
                                            {instance.responseSelection?.xPath
                                              ? 'Xpath:'
                                              : 'JsonPath:'}
                                          </Text>

                                          <Input
                                            value={
                                              instance.responseSelection?.xPath
                                                ? instance.responseSelection.xPath
                                                : instance.responseSelection.jsonPath
                                            }
                                            style={{
                                              color: 'black',
                                              borderRadius: '15px',
                                              border: '1px solid #D0D5DD',
                                            }}
                                            px={5}
                                            py={6}
                                            borderRadius={15}
                                            onChange={(e) => {
                                              if (instance.responseSelection.xPath) {
                                                dispatch(
                                                  updateResponseSelection({
                                                    index: i,
                                                    responseSelection: {
                                                      xPath: e.target.value,
                                                      jsonPath:
                                                        instance.responseSelection
                                                          .jsonPath,
                                                      responseMatch:
                                                        instance.responseSelection
                                                          .responseMatch,
                                                      invariant: instance.invariant,
                                                      matchType:
                                                        instance.responseSelection
                                                          .matchType,
                                                    },
                                                  })
                                                );
                                              } else {
                                                dispatch(
                                                  updateResponseSelection({
                                                    index: i,
                                                    responseSelection: {
                                                      xPath:
                                                        instance.responseSelection.xPath,
                                                      jsonPath: e.target.value,
                                                      responseMatch:
                                                        instance.responseSelection
                                                          .responseMatch,
                                                      invariant: instance.invariant,
                                                      matchType:
                                                        instance.responseSelection
                                                          .matchType,
                                                    },
                                                  })
                                                );
                                              }
                                            }}
                                          />
                                        </Flex>
                                      </Flex>
                                    </CardBody>
                                  </Card>
                                ))}
                              </Flex>
                            </Flex>
                          </Flex>
                        </Box>
                      </Flex>
                    </Box>
                  </Flex>
                  <Flex direction="column">
                    <Text color="gray.600" className="small-font" ml="5px">
                      JS Injection
                    </Text>
                    <Flex alignItems="center" gap={5}>
                      <Textarea
                        w="400px"
                        px={5}
                        py={6}
                        borderRadius={15}
                        placeholder="JS injection to run on website"
                        value={customInjection}
                        onChange={(e: { target: { value: SetStateAction<string> } }) =>
                          setCustomInjection(e.target.value)
                        }
                      />
                      <Tooltip
                        whiteSpace="pre-line"
                        label="Providers with Custom Injection are subjected to admin approval"
                      >
                        <Icon
                          as={QuestionOutlineIcon}
                          w={6}
                          h={6}
                          color="gray.600"
                          cursor="pointer"
                        />
                      </Tooltip>
                    </Flex>
                  </Flex>
                </>
              )}
            </Flex>
          </VStack>
        </CardBody>
      </Card>
      <Box w="full" backgroundColor="gray.50" px={30} py={4}>
        <Flex w="full" justifyContent="space-between">
          <Button
            backgroundColor="transparent"
            border="1px solid gray"
            borderRadius={10}
            fontWeight="normal"
            _hover={{
              fontWeight: 'bold',
              transform: 'scale(1.05)',
            }}
            isDisabled={buttonLoading}
            onClick={handleGoBack}
          >
            Back
          </Button>

          <Button
            backgroundColor="black"
            color="white"
            border="1px solid black"
            borderRadius={10}
            fontWeight="normal"
            _hover={{
              transform: 'scale(1.05)',
            }}
            isLoading={buttonLoading}
            onClick={handleNext}
          >
            Next
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
};
